import React, { useCallback } from "react";

const OUTCOME_FILTERS: [Result | "AI Insight", string][] = [
    ["W", "Winner"],
    ["L", "Loser"],
    ["Fault", "Fault"],
    ["Finish", "Finish"],
    ["AI Insight", "AI Insight"],
];

const SHOT_TYPE_FILTERS = [
    "Forehand",
    "Backhand",
    "Volley",
    "Lob",
    "server",
    "Overhead",
];

interface Props {
    players: [Team, Team];
    selectedFilters: SelectedFilters;
    onChange: (selectedFilters: SelectedFilters) => void;
}

export default function Filters({
    players,
    selectedFilters,
    onChange,
}: Props): JSX.Element {
    const onCheck = useCallback((filterType: FilterType, value: string, selected: boolean) => {
        onChange({
            ...selectedFilters,
            [filterType]: selected
                ? selectedFilters[filterType].filter((f) => f !== value)
                : [...selectedFilters[filterType], value],
        });
    }, [selectedFilters, onChange]);

    return (
        <div className="grid grid-cols-3 rounded-lg bg-blue-zodiac-800 p-2">
            <div>
                {players.flatMap((team) => team.map((player) => (
                    <CheckControl
                        key={player}
                        value={player}
                        name="player"
                        label={player}
                        checked={selectedFilters.player.includes(player)}
                        onChange={onCheck}
                    />
                )))}
            </div>
            <div>
                {OUTCOME_FILTERS.map(([outcome, label]) => (
                    <CheckControl
                        key={outcome}
                        value={outcome}
                        name="outcome"
                        label={label}
                        checked={selectedFilters.outcome.includes(outcome)}
                        onChange={onCheck}
                    />
                ))}
            </div>
            <div>
                {SHOT_TYPE_FILTERS.map((type) => (
                    <CheckControl
                        key={type}
                        value={type}
                        name="shot"
                        label={type === "server" ? "Fault" : type}
                        checked={selectedFilters.shot.includes(type)}
                        onChange={onCheck}
                    />
                ))}
            </div>
        </div>
    );
}

interface CheckControlProps {
    name: keyof FilterOptions;
    label: string;
    value: string;
    checked: boolean;
    onChange: (filterType: FilterType, value: string, toggle: boolean) => void;
}

function CheckControl({
    name, label, value, checked, onChange,
}: CheckControlProps): JSX.Element {
    return (
        <div className="relative flex gap-x-3 py-2">
            <div className="flex h-6 items-center">
                <input
                    name={value}
                    id={value}
                    checked={checked}
                    type="checkbox"
                    className="h-6 w-6 rounded border-gray-300 text-blue-zodiac-500 focus:ring-blue-zodiac-500"
                    value={value}
                    onChange={() => onChange(name, value, checked)}
                />
            </div>
            <div className="text-xs leading-6">
                <label htmlFor={value} className="font-medium">{label}</label>
            </div>
        </div>
    );
}
